import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';
import { RdLocalizedNumberPipe } from '../../../pipes/rd-localized-number.pipe';
import { WidgetDynamicBaseComponent } from '../../widgetDynamicBase';

@Component({
  selector: 'investor-surveys-widget',
  templateUrl: './investor-surveys-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './investor-surveys-widget.component.scss']
})
export class InvestorSurveysWidgetComponent extends WidgetDynamicBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("investorSurveys", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.mockupData$ = this.widgetService.loadMockup('investorSurveys').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('investorSurveys', this.getReport()).pipe();
  }

  /**
   * Constructor
   * @param injector
   */
  constructor(injector: Injector, private rdLocalizedNumberPipe: RdLocalizedNumberPipe) {
    super(injector, 'investorSurveys', false);
  }

  /**
 * Initializes the component
 */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Pre-process the loaded data.Override if needed
   * @param data The loaded data
   */
  protected preprocessLoadedData(data): any {
    const rows = data.rows;
    const length = rows.length;
    if (length > 0) {
      /*
        here we calculate how many rows are "covered" by areaName, propertyType and source (they are merged cells in import excel)
      */

      let currentRow = rows[length - 1];
      currentRow.areaNameRows = 1;
      currentRow.propertyTypeRows = 1;
      currentRow.sourceRows = 1;
      let currentAreaName = currentRow.areaName;
      let currentPropertyType = currentRow.propertyType;
      let currentSource = currentRow.source;

      for (let idx = length - 1; idx > 0; idx--) {
        let areaNameChanged = false
          , propertyTypeChanged = false;

        const previousRow = rows[idx - 1];

        if (previousRow.areaName === currentAreaName) {
          previousRow.areaNameRows = currentRow.areaNameRows + 1;
          currentRow.areaNameRows = 0;
        } else {
          areaNameChanged = true;
          previousRow.areaNameRows = 1;
        }

        if (!areaNameChanged && previousRow.propertyType === currentPropertyType) {
          previousRow.propertyTypeRows = currentRow.propertyTypeRows + 1;
          currentRow.propertyTypeRows = 0;
        } else {
          propertyTypeChanged = true;
          previousRow.propertyTypeRows = 1;
        }

        if (!propertyTypeChanged && previousRow.source === currentSource) {
          previousRow.sourceRows = currentRow.sourceRows + 1;
          currentRow.sourceRows = 0;
        } else {
          propertyTypeChanged = true;
          previousRow.sourceRows = 1;
        }

        // let the current be previous for next loop
        currentRow = previousRow;
        // save current values
        currentAreaName = currentRow.areaName;
        currentPropertyType = currentRow.propertyType;
        currentSource = currentRow.source;
      }

      // calculate capRateFormatted (can be just number if in capRate property or range if capRate and capRateMin and capRateMax exists)
      for (const row of rows) {
        if (row.capRate) {
          row.capRateFormatted = this.rdLocalizedNumberPipe.transform(row.capRate * 100, 2, false, '%', '', '');
        } else if (row.capRateMin && row.capRateMax) {
          const min = this.rdLocalizedNumberPipe.transform(row.capRateMin * 100, 2, false, '%', '', '');
          const max = this.rdLocalizedNumberPipe.transform(row.capRateMax * 100, 2, false, '%', '', '');

          row.capRateFormatted = `${min} - ${max}`;
        } else {
          row.capRateFormatted = '';
        }
      }

      // group by areaName so it can be displayed
      const uniqueAreasSet = new Set(rows.map(r => r.areaName));
      data.areas = Array.from(uniqueAreasSet).map((areaName) => ({ areaName, rows: rows.filter(r => r.areaName === areaName) }));
    }

    let currentIndex = 4;
    for (let i = 0; i < data.areas?.length; ++i) {
      data.areas[i].rowIndex = currentIndex;
      ++currentIndex;
      for (let j = 0; j < data.areas[i].rows?.length; ++j) {
        data.areas[i].rows[j].rowIndex = currentIndex;
        ++currentIndex;
      }
    }

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    const data = ObjectUtil.deepCopy(this.widgetData);//deep copy

    return data;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  public getMinimumRowIndex(): number {
    return 1;
  }

  public getMaximumRowIndex(): number {
    if (this.widgetData) {
      let totalSubRows = 0;
      for (const area of this.widgetData.areas) {
        totalSubRows += area.rows.length;
      }
      return this.widgetData.areas?.length + totalSubRows + 3;
    }
    return -1;
  }

  public moreColumnsRequestedIfOnPortraitPage(): boolean {
    return false;
  }
}
